import { makeEventSource, CommentsApiError } from '@liveblocks/core';
import { useRoom } from '@liveblocks/react';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim/index.js';
import { isComposerBodyAutoLink } from '../../slate/plugins/auto-links.mjs';
import { isComposerBodyCustomLink } from '../../slate/plugins/custom-links.mjs';
import { isComposerBodyMention } from '../../slate/plugins/mentions.mjs';
import { isText } from '../../slate/utils/is-text.mjs';
import { getFiles } from '../../utils/data-transfer.mjs';
import { exists } from '../../utils/exists.mjs';
import { useInitial } from '../../utils/use-initial.mjs';
import { useLatest } from '../../utils/use-latest.mjs';
import { isCommentBodyMention, isCommentBodyLink, isCommentBodyText } from '../Comment/utils.mjs';
import { useComposer, useComposerAttachmentsContext } from './contexts.mjs';

function composerBodyMentionToCommentBodyMention(mention) {
  return {
    type: "mention",
    id: mention.id
  };
}
function composerBodyAutoLinkToCommentBodyLink(link) {
  return {
    type: "link",
    url: link.url
  };
}
function composerBodyCustomLinkToCommentBodyLink(link) {
  return {
    type: "link",
    url: link.url,
    text: link.children.map((child) => child.text).join("")
  };
}
function commentBodyMentionToComposerBodyMention(mention) {
  return {
    type: "mention",
    id: mention.id,
    children: [{ text: "" }]
  };
}
function commentBodyLinkToComposerBodyLink(link) {
  if (link.text) {
    return {
      type: "custom-link",
      url: link.url,
      children: [{ text: link.text }]
    };
  } else {
    return {
      type: "auto-link",
      url: link.url,
      children: [{ text: link.url }]
    };
  }
}
function composerBodyToCommentBody(body) {
  return {
    version: 1,
    content: body.map((block) => {
      if (block.type !== "paragraph") {
        return null;
      }
      const children = block.children.map((inline) => {
        if (isComposerBodyMention(inline)) {
          return composerBodyMentionToCommentBodyMention(inline);
        }
        if (isComposerBodyAutoLink(inline)) {
          return composerBodyAutoLinkToCommentBodyLink(inline);
        }
        if (isComposerBodyCustomLink(inline)) {
          return composerBodyCustomLinkToCommentBodyLink(inline);
        }
        if (isText(inline)) {
          return inline;
        }
        return null;
      }).filter(exists);
      return {
        ...block,
        children
      };
    }).filter(exists)
  };
}
const emptyComposerBody = [];
function commentBodyToComposerBody(body) {
  if (!body || !body?.content) {
    return emptyComposerBody;
  }
  return body.content.map((block) => {
    if (block.type !== "paragraph") {
      return null;
    }
    const children = block.children.map((inline) => {
      if (isCommentBodyMention(inline)) {
        return commentBodyMentionToComposerBodyMention(inline);
      }
      if (isCommentBodyLink(inline)) {
        return commentBodyLinkToComposerBodyLink(inline);
      }
      if (isCommentBodyText(inline)) {
        return inline;
      }
      return null;
    }).filter(exists);
    return {
      ...block,
      children
    };
  }).filter(exists);
}
function getPlacementFromPosition(position, direction = "ltr") {
  return `${position}-${direction === "rtl" ? "end" : "start"}`;
}
function getSideAndAlignFromPlacement(placement) {
  const [side, align = "center"] = placement.split("-");
  return [side, align];
}
function useComposerAttachmentsDropArea({
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  disabled
}) {
  const { isDisabled: isComposerDisabled } = useComposer();
  const isDisabled = isComposerDisabled || disabled;
  const { createAttachments } = useComposerAttachmentsContext();
  const [isDraggingOver, setDraggingOver] = useState(false);
  const latestIsDraggingOver = useLatest(isDraggingOver);
  const handleDragEnter = useCallback(
    (event) => {
      onDragEnter?.(event);
      if (latestIsDraggingOver.current || isDisabled || event.isDefaultPrevented()) {
        return;
      }
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer.types.includes("Files")) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDraggingOver(true);
    },
    [onDragEnter, isDisabled]
  );
  const handleDragLeave = useCallback(
    (event) => {
      onDragLeave?.(event);
      if (!latestIsDraggingOver.current || isDisabled || event.isDefaultPrevented()) {
        return;
      }
      if (event.relatedTarget ? event.relatedTarget === event.currentTarget || event.currentTarget.contains(event.relatedTarget) : event.currentTarget !== event.target) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDraggingOver(false);
    },
    [onDragLeave, isDisabled]
  );
  const handleDragOver = useCallback(
    (event) => {
      onDragOver?.(event);
      if (isDisabled || event.isDefaultPrevented()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [onDragOver, isDisabled]
  );
  const handleDrop = useCallback(
    (event) => {
      onDrop?.(event);
      if (!latestIsDraggingOver.current || isDisabled || event.isDefaultPrevented()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDraggingOver(false);
      const files = getFiles(event.dataTransfer);
      createAttachments(files);
    },
    [onDrop, isDisabled, createAttachments]
  );
  return [
    isDraggingOver,
    {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      "data-drop": isDraggingOver ? "" : void 0,
      "data-disabled": isDisabled ? "" : void 0
    }
  ];
}
class AttachmentTooLargeError extends Error {
  constructor(message, origin = "client") {
    super(message);
    this.name = "AttachmentTooLargeError";
    this.origin = origin;
  }
}
function createComposerAttachmentsManager(room, options) {
  const attachments = /* @__PURE__ */ new Map();
  const abortControllers = /* @__PURE__ */ new Map();
  const eventSource = makeEventSource();
  let cachedSnapshot = null;
  function notifySubscribers() {
    cachedSnapshot = null;
    eventSource.notify();
  }
  function uploadAttachment(attachment) {
    const abortController = new AbortController();
    abortControllers.set(attachment.id, abortController);
    room.uploadAttachment(attachment, {
      signal: abortController.signal
    }).then(() => {
      attachments.set(attachment.id, {
        ...attachment,
        status: "uploaded"
      });
      notifySubscribers();
    }).catch((error) => {
      if (error instanceof Error && error.name !== "AbortError" && error.name !== "TimeoutError") {
        attachments.set(attachment.id, {
          ...attachment,
          status: "error",
          error: error instanceof CommentsApiError && error.status === 413 ? new AttachmentTooLargeError("File is too large.", "server") : error
        });
        notifySubscribers();
      }
    });
  }
  function addAttachments(addedAttachments) {
    if (addedAttachments.length === 0) {
      return;
    }
    const newAttachments = addedAttachments.filter(
      (attachment) => !attachments.has(attachment.id)
    );
    const attachmentsToUpload = [];
    for (const attachment of newAttachments) {
      if (attachment.type === "localAttachment") {
        if (attachment.file.size > options.maxFileSize) {
          attachments.set(attachment.id, {
            ...attachment,
            status: "error",
            error: new AttachmentTooLargeError("File is too large.", "client")
          });
          continue;
        }
        attachments.set(attachment.id, {
          ...attachment,
          status: "uploading"
        });
        attachmentsToUpload.push(attachment);
      } else {
        attachments.set(attachment.id, attachment);
      }
    }
    if (newAttachments.length > 0) {
      notifySubscribers();
    }
    for (const attachment of attachmentsToUpload) {
      uploadAttachment(attachment);
    }
  }
  function removeAttachment(attachmentId) {
    const abortController = abortControllers.get(attachmentId);
    abortController?.abort();
    attachments.delete(attachmentId);
    abortControllers.delete(attachmentId);
    notifySubscribers();
  }
  function getSnapshot() {
    if (!cachedSnapshot) {
      cachedSnapshot = Array.from(attachments.values());
    }
    return cachedSnapshot;
  }
  function clear() {
    abortControllers.forEach((controller) => controller.abort());
    abortControllers.clear();
    attachments.clear();
    notifySubscribers();
  }
  return {
    addAttachments,
    removeAttachment,
    getSnapshot,
    subscribe: eventSource.subscribe,
    clear
  };
}
function preventBeforeUnloadDefault(event) {
  event.preventDefault();
}
function useComposerAttachmentsManager(defaultAttachments, options) {
  const room = useRoom();
  const frozenDefaultAttachments = useInitial(defaultAttachments);
  const frozenAttachmentsManager = useInitial(
    () => createComposerAttachmentsManager(room, options)
  );
  useEffect(() => {
    frozenAttachmentsManager.addAttachments(frozenDefaultAttachments);
  }, [frozenDefaultAttachments, frozenAttachmentsManager]);
  useEffect(() => {
    return () => {
      frozenAttachmentsManager.clear();
    };
  }, [frozenAttachmentsManager]);
  const attachments = useSyncExternalStore(
    frozenAttachmentsManager.subscribe,
    frozenAttachmentsManager.getSnapshot,
    frozenAttachmentsManager.getSnapshot
  );
  const isUploadingAttachments = useMemo(() => {
    return attachments.some(
      (attachment) => attachment.type === "localAttachment" && attachment.status === "uploading"
    );
  }, [attachments]);
  useEffect(() => {
    if (!isUploadingAttachments) {
      return;
    }
    window.addEventListener("beforeunload", preventBeforeUnloadDefault);
    return () => {
      window.removeEventListener("beforeunload", preventBeforeUnloadDefault);
    };
  }, [isUploadingAttachments]);
  return {
    attachments,
    isUploadingAttachments,
    addAttachments: frozenAttachmentsManager.addAttachments,
    removeAttachment: frozenAttachmentsManager.removeAttachment,
    clearAttachments: frozenAttachmentsManager.clear
  };
}

export { AttachmentTooLargeError, commentBodyLinkToComposerBodyLink, commentBodyMentionToComposerBodyMention, commentBodyToComposerBody, composerBodyAutoLinkToCommentBodyLink, composerBodyCustomLinkToCommentBodyLink, composerBodyMentionToCommentBodyMention, composerBodyToCommentBody, getPlacementFromPosition, getSideAndAlignFromPlacement, useComposerAttachmentsDropArea, useComposerAttachmentsManager };
//# sourceMappingURL=utils.mjs.map
